import React, { useState } from "react";
import ModuleHeaderMenu from "../components/ModuleHeaderMenu";
import { Btn, Badge, Modal, StatCard } from "../components/ui";
import { CC_COLORS, T} from "../lib/globals";
import { fmtDate, fmtMoney } from "../lib/formatters";
import { makeSeed } from "../lib/seed";
import { isDeletedItem } from "../lib/syncUtils";
import { getPriorityConfig } from "../components/ServicesUI";

export function createSedeModule(deps) {
  const {
    useFarmCollection,
    canViewCostCenter,
    normalizeServiceType,
    deriveAllowedModules,
    normalizeModulePermissions,
  } = deps;

  function SedeModule({ session, isGestor, onNavigate }){
  const seed = makeSeed();
  const [tab,setTab] = useState("overview");
  const moduleCardsConfig = (()=>{
    try{
      const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
      return parsed && typeof parsed === "object" ? parsed : {};
    }catch(_){ return {}; }
  })();
  const [orders] = useFarmCollection("orders", seed.orders);
  const [services] = useFarmCollection("services", []);
  const [occurrences] = useFarmCollection("occurrences", []);
  const [inspections] = useFarmCollection("inspections", []);
  const [pastures] = useFarmCollection("pastures", []);
  const [poi] = useFarmCollection("poi", []);
  const [selected,setSelected] = useState(null);

  const isSede = (value)=> String(value||"").trim().toLowerCase()==="sede";
  const canViewSedeLocations = canViewCostCenter(session, "pastures", "Sede") || canViewCostCenter(session, "poi", "Sede");
  const canViewSedePurchases = canViewCostCenter(session, "purchases", "Sede");
  const canViewSedeServices = canViewCostCenter(session, "services", "Sede") || canViewCostCenter(session, "services_external", "Sede");
  const canViewSedeOccurrences = canViewCostCenter(session, "occurrences", "Sede");
  const canViewSedeInspections = canViewCostCenter(session, "inspections", "Sede");
  const deniedScopes = [
    !canViewSedeLocations ? "Localização" : null,
    !canViewSedePurchases ? "Compras" : null,
    !canViewSedeServices ? "Serviços" : null,
    !canViewSedeOccurrences ? "Ocorrências" : null,
    !canViewSedeInspections ? "Vistorias" : null,
  ].filter(Boolean);
  const activeOrders = canViewSedePurchases ? (orders||[]).filter(item=>!isDeletedItem(item) && isSede(item?.costCenter)) : [];
  const internalServices = canViewSedeServices ? (services||[]).filter(item=>!isDeletedItem(item) && isSede(item?.costCenter) && normalizeServiceType(item)!=="external") : [];
  const externalServices = canViewSedeServices ? (services||[]).filter(item=>!isDeletedItem(item) && isSede(item?.costCenter) && normalizeServiceType(item)==="external") : [];
  const sedeOccurrences = canViewSedeOccurrences ? (occurrences||[]).filter(item=>!isDeletedItem(item) && isSede(item?.costCenter)) : [];
  const sedeInspections = canViewSedeInspections ? (inspections||[]).filter(item=>!isDeletedItem(item) && isSede(item?.costCenter)) : [];
  const sedeLocations = canViewSedeLocations ? [
    ...(pastures||[]).filter(item=>!isDeletedItem(item) && isSede(item?.costCenter)),
    ...(poi||[]).filter(item=>!isDeletedItem(item) && isSede(item?.costCenter)),
  ] : [];

  const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
    const moduleConfig = moduleCardsConfig?.sede;
    if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, sectionId)){
      const configured = moduleConfig?.[sectionId];
      if(Array.isArray(configured)) return configured.map(v=>String(v||""));
      return [];
    }
    return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||""));
  }, [moduleCardsConfig]);
  const allowedSedeCards = React.useMemo(()=>{
    const direct = getConfiguredSectionCards("moduleHome", []);
    return Array.isArray(direct) && direct.length ? direct : ["visao_geral","compras_sede","servicos_sede","ocorrencias_sede"];
  }, [getConfiguredSectionCards]);

  const overviewCards = [
    {id:"visao_geral", label:"Visão Geral", value:[canViewSedeLocations,canViewSedePurchases,canViewSedeServices,canViewSedeOccurrences,canViewSedeInspections].filter(Boolean).length, color:T.green, onClick:()=>setTab("overview")},
    {id:"compras_sede", label:"Compras", value:activeOrders.length, color:T.orange, onClick:()=>setTab("purchases")},
    {id:"servicos_sede", label:"Serviços", value:internalServices.length + externalServices.length, color:T.accent, onClick:()=>setTab("internal")},
    {id:"ocorrencias_sede", label:"Ocorrências", value:sedeOccurrences.length, color:T.red, onClick:()=>setTab("occurrences")},
  ].filter(card=>allowedSedeCards.includes(card.id));

  const buildBridgeSession = React.useCallback((moduleId)=>{
    const current = normalizeModulePermissions(session?.modulePermissions, session?.allowedModules, session?.role);
    const next = { ...current };
    const allow = { view:true, create:true, edit:true, delete:true };
    next.sede = allow;
    next[moduleId] = allow;
    if(moduleId==="services_external") next.services = allow;
    if(moduleId==="poi") next.pastures = allow;
    if(moduleId==="pastures") next.poi = allow;
    return {
      ...session,
      modulePermissions: next,
      allowedModules: deriveAllowedModules(next, session?.role),
      sedeBridge: {
        enabled:true,
        costCenter:"Sede",
        sourceModule:"sede",
        targetModule:moduleId,
      },
    };
  }, [session]);

  const openOriginal = (moduleId, params={})=>{
    if(onNavigate) onNavigate(moduleId, { ...params, sessionOverride: buildBridgeSession(moduleId), forcedCostCenter:"Sede", sourceModule:"sede" });
  };

  const TabBtn = ({id,label})=><Btn size="sm" variant={tab===id?"primary":"ghost"} onClick={()=>setTab(id)}>{label}</Btn>;
  const Row = ({title,subtitle,badges=[],onClick})=><button type="button" onClick={onClick} style={{width:"100%",textAlign:"left",padding:14,borderRadius:14,border:`1px solid ${T.border}`,background:T.card,cursor:"pointer"}}>
    <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"flex-start",flexWrap:"wrap"}}>
      <div style={{minWidth:0,flex:1}}>
        <div style={{fontWeight:800,color:T.text,fontSize:14,wordBreak:"break-word"}}>{title}</div>
        {!!subtitle&&<div style={{fontSize:12,color:T.muted,marginTop:4,wordBreak:"break-word"}}>{subtitle}</div>}
      </div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end"}}>{badges.map((item,idx)=><Badge key={idx} color={item.color||T.accent}>{item.label}</Badge>)}</div>
    </div>
  </button>;

  const renderList = (items, mapItem, emptyText)=> items.length ? <div style={{display:"grid",gap:10}}>{items.map(mapItem)}</div> : <div style={{fontSize:13,color:T.muted}}>{emptyText}</div>;

  return <div>
    <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"center",flexWrap:"wrap",marginBottom:16}}>
      <div>
        <h2 style={{margin:0,color:T.text,fontSize:20,fontWeight:800}}>🏠 Módulo Sede</h2>
        <div style={{fontSize:13,color:T.muted,marginTop:4}}>Central simples para tudo do centro de custo Sede, integrada aos módulos originais e com acesso focado na própria Sede.</div>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <Badge color={T.accent}>Operação restrita à Sede</Badge>
      </div>
    </div>

    {deniedScopes.length>0 && <div style={{marginBottom:12,padding:"10px 14px",borderRadius:10,background:`${T.orange||T.yellow}18`,border:`1px solid ${(T.orange||T.yellow)}44`,fontSize:12,color:T.text}}>Acesso parcial no módulo Sede. Sem permissão para: <strong>{deniedScopes.join(", ")}</strong>. Esses blocos ficam ocultos.</div>}

    <ModuleHeaderMenu
      T={T}
      title="Menu da Sede"
      items={[
        { id:"overview", label:"Resumo", icon:"🏠", active:tab==="overview", onClick:()=>setTab("overview") },
        { id:"locations", label:"Localização", icon:"🗺️", active:tab==="locations", onClick:()=>setTab("locations") },
        { id:"purchases", label:"Compras", icon:"🛒", active:tab==="purchases", onClick:()=>setTab("purchases") },
        { id:"internal", label:"Serviços Internos", icon:"🔨", active:tab==="internal", onClick:()=>setTab("internal") },
        { id:"external", label:"Serviços Terceirizados", icon:"👷", active:tab==="external", onClick:()=>setTab("external") },
        { id:"occurrences", label:"Ocorrências", icon:"🚨", active:tab==="occurrences", onClick:()=>setTab("occurrences") },
        { id:"inspections", label:"Vistorias", icon:"🔍", active:tab==="inspections", onClick:()=>setTab("inspections") },
      ]}
    />

    <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
      <TabBtn id="overview" label="Resumo" />
      <TabBtn id="locations" label="Localização" />
      <TabBtn id="purchases" label="Compras" />
      <TabBtn id="internal" label="Serviços Internos" />
      <TabBtn id="external" label="Serviços Terceirizados" />
      <TabBtn id="occurrences" label="Ocorrências" />
      <TabBtn id="inspections" label="Vistorias" />
    </div>

    {tab==="overview" && !!overviewCards.length && <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,marginBottom:14}}>
      {overviewCards.map(card=><StatCard key={card.id} icon="🏠" label={card.label} value={card.value} color={card.color} onClick={card.onClick} />)}
    </div>}

    {tab==="locations" && <div style={{display:"grid",gap:12}}>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><Btn size="sm" onClick={()=>openOriginal("pastures", { sedeFocus:true })}>Abrir Localização da Sede</Btn></div>
      {renderList(sedeLocations, (item,idx)=><Row key={`${item.id||idx}`} title={item.name||item.title||item.label||"Localização Sede"} subtitle={[item.type||item.category||"Registro", item.description||item.notes||""].filter(Boolean).join(" · ")} badges={[{label:item.costCenter||"Sede", color:CC_COLORS[item.costCenter||"Sede"]||T.accent}]} onClick={()=>setSelected({type:"Localização", moduleId:"pastures", data:item})} />, "Nenhum registro de localização da Sede encontrado.")}
    </div>}

    {tab==="purchases" && <div style={{display:"grid",gap:12}}>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><Btn size="sm" onClick={()=>openOriginal("purchases", { sedeFocus:true })}>Abrir Compras da Sede</Btn></div>
      {renderList(activeOrders, item=><Row key={item.id} title={item.title||item.name||item.category||"Compra Sede"} subtitle={[item.supplierName||item.supplier||"Fornecedor não informado", fmtDate(item.date||item.createdAt), item.status||"Sem status"].filter(Boolean).join(" · ")} badges={[{label:item.costCenter||"Sede", color:CC_COLORS[item.costCenter||"Sede"]||T.accent},{label:fmtMoney(Number(item.total||item.totalValue||item.value||0)), color:T.green}]} onClick={()=>setSelected({type:"Compra", moduleId:"purchases", data:item, openParams:{openOrderId:item.id}})} />, "Nenhuma compra da Sede encontrada.")}
    </div>}

    {tab==="internal" && <div style={{display:"grid",gap:12}}>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><Btn size="sm" onClick={()=>openOriginal("services", { sedeFocus:true, servicesTabPreset:"internal" })}>Abrir Serviços Internos da Sede</Btn></div>
      {renderList(internalServices, item=><Row key={item.id} title={item.title||"Serviço Interno"} subtitle={[item.category||item.serviceType||"Serviço", item.location||item.where||"", item.status||"Sem status"].filter(Boolean).join(" · ")} badges={[{label:item.priority||"Normal", color:getPriorityConfig(item.priority||"Normal").color||T.accent},{label:item.costCenter||"Sede", color:CC_COLORS[item.costCenter||"Sede"]||T.accent}]} onClick={()=>setSelected({type:"Serviço Interno", moduleId:"services", data:item})} />, "Nenhum serviço interno da Sede encontrado.")}
    </div>}

    {tab==="external" && <div style={{display:"grid",gap:12}}>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><Btn size="sm" onClick={()=>openOriginal("services_external", { sedeFocus:true, servicesTabPreset:"external" })}>Abrir Terceirizados da Sede</Btn></div>
      {renderList(externalServices, item=><Row key={item.id} title={item.title||"Serviço Terceirizado"} subtitle={[item.providerName||item?.budget?.provider||"Prestador não informado", item.status||"Sem status", fmtDate(item.date||item.createdAt)].filter(Boolean).join(" · ")} badges={[{label:item.costCenter||"Sede", color:CC_COLORS[item.costCenter||"Sede"]||T.accent},{label:fmtMoney(Number(item?.budget?.value||item.finalValue||0)), color:T.green}]} onClick={()=>setSelected({type:"Serviço Terceirizado", moduleId:"services_external", data:item})} />, "Nenhum serviço terceirizado da Sede encontrado.")}
    </div>}

    {tab==="occurrences" && <div style={{display:"grid",gap:12}}>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><Btn size="sm" onClick={()=>openOriginal("occurrences", { sedeFocus:true })}>Abrir Ocorrências da Sede</Btn></div>
      {renderList(sedeOccurrences, item=><Row key={item.id} title={item.title||item.description||"Ocorrência"} subtitle={[item.category||"Ocorrência", item.location||"", item.status||"Sem status"].filter(Boolean).join(" · ")} badges={[{label:item.costCenter||"Sede", color:CC_COLORS[item.costCenter||"Sede"]||T.accent},{label:item.priority||"Normal", color:getPriorityConfig(item.priority||"Normal").color||T.accent}]} onClick={()=>setSelected({type:"Ocorrência", moduleId:"occurrences", data:item})} />, "Nenhuma ocorrência da Sede encontrada.")}
    </div>}

    {tab==="inspections" && <div style={{display:"grid",gap:12}}>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><Btn size="sm" onClick={()=>openOriginal("inspections", { sedeFocus:true })}>Abrir Vistorias da Sede</Btn></div>
      {renderList(sedeInspections, item=><Row key={item.id} title={item.title||item.name||item.targetName||"Vistoria"} subtitle={[item.targetLabel||item.targetType||item.category||"Vistoria", item.status||"Sem status", fmtDate(item.date||item.createdAt)].filter(Boolean).join(" · ")} badges={[{label:item.costCenter||"Sede", color:CC_COLORS[item.costCenter||"Sede"]||T.accent}]} onClick={()=>setSelected({type:"Vistoria", moduleId:"inspections", data:item})} />, "Nenhuma vistoria da Sede encontrada.")}
    </div>}

    <Modal open={!!selected} onClose={()=>setSelected(null)} title={selected ? `${selected.type} · Sede` : ""}>
      {!selected ? null : <div style={{display:"grid",gap:12}}>
        <div style={{padding:14,borderRadius:14,border:`1px solid ${T.border}`,background:T.card2}}>
          <div style={{fontWeight:800,color:T.text,fontSize:16,marginBottom:8}}>{selected.data?.title||selected.data?.name||selected.data?.description||"Registro"}</div>
          <div style={{display:"grid",gap:6,fontSize:13,color:T.muted}}>
            {Object.entries(selected.data||{}).filter(([key,val])=>val!==undefined && val!==null && val!=="" && typeof val !== "object").slice(0,12).map(([key,val])=><div key={key}><strong style={{color:T.text}}>{key}:</strong> {String(val)}</div>)}
          </div>
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",flexWrap:"wrap"}}>
          <Btn variant="ghost" onClick={()=>setSelected(null)}>Fechar</Btn>
          <Btn onClick={()=>{ const params = selected.openParams || {}; openOriginal(selected.moduleId, params); setSelected(null); }}>Abrir no módulo original</Btn>
        </div>
      </div>}
    </Modal>
  </div>;
}


  return SedeModule;
}
